/**
 * SimplePractice historical migration DocumentReferences — detection, EoC scoping,
 * and mapping to native Medical Record type labels for chart timeline display.
 */

import type { DocumentReference } from '@medplum/fhirtypes';

import {
  EPISODE_OF_CARE_CONTEXT_EXT,
  HISTORICAL_MIGRATION_TAG_CODES,
  HISTORICAL_MIGRATION_TAG_SYSTEM,
  SP_HISTORICAL_DOCUMENT_TYPE_CODES,
} from '../constants';
import { getDocumentType, isPsychotherapyNote } from './document-reference';
import { isHistoricalChartDocumentReference } from './historical-document-builders';

/** Canonical Medical Record filter / presentation labels for imported SP PDFs. */
export type HistoricalMedicalRecordType =
  | 'Progress Notes'
  | 'Intake Note'
  | 'Assessments'
  | 'Chart Notes'
  | 'Treatment Plan'
  | 'Intake Forms'
  | 'Psychotherapy Notes';

/**
 * Maps immutable SP historical `DOCUMENT_TYPE_SYSTEM` codes to the same record-type
 * strings native chart rows use (appointments, measures, treatment planning, forms,
 * client operations).
 */
export const HISTORICAL_DOCUMENT_TYPE_TO_MEDICAL_RECORD_TYPE: Record<string, HistoricalMedicalRecordType> = {
  [SP_HISTORICAL_DOCUMENT_TYPE_CODES.progressNote]: 'Progress Notes',
  [SP_HISTORICAL_DOCUMENT_TYPE_CODES.intakeNote]: 'Intake Note',
  [SP_HISTORICAL_DOCUMENT_TYPE_CODES.mentalStatusExam]: 'Assessments',
  [SP_HISTORICAL_DOCUMENT_TYPE_CODES.chartNote]: 'Chart Notes',
  [SP_HISTORICAL_DOCUMENT_TYPE_CODES.assessmentNote]: 'Assessments',
  [SP_HISTORICAL_DOCUMENT_TYPE_CODES.treatmentPlan]: 'Treatment Plan',
  [SP_HISTORICAL_DOCUMENT_TYPE_CODES.form]: 'Intake Forms',
  [SP_HISTORICAL_DOCUMENT_TYPE_CODES.uploadedDocument]: 'Intake Forms',
  // historical-psychotherapy-note intentionally omitted — private notes must not appear on Medical Record
};

/**
 * True when tagged as a historical SP/migration import.
 * Private `psychotherapy-note` PDFs may also carry the tag for provenance but must
 * not appear on the Medical Record — use {@link isHistoricalChartDocumentReference}.
 */
export function isHistoricalMigrationDocumentReference(doc: DocumentReference): boolean {
  return (
    doc.meta?.tag?.some(
      (t) =>
        t.system === HISTORICAL_MIGRATION_TAG_SYSTEM &&
        t.code === HISTORICAL_MIGRATION_TAG_CODES.historical,
    ) ?? false
  );
}

/** Chart-visible historical DocRefs (excludes private psychotherapy notes). */
export function isHistoricalChartMigrationDocumentReference(doc: DocumentReference): boolean {
  return isHistoricalChartDocumentReference(doc) && !isPsychotherapyNote(doc);
}

export function getHistoricalMedicalRecordType(
  doc: DocumentReference,
): HistoricalMedicalRecordType | undefined {
  const typeCode = getDocumentType(doc);
  if (!typeCode) return undefined;
  return HISTORICAL_DOCUMENT_TYPE_TO_MEDICAL_RECORD_TYPE[typeCode];
}

/** Resolve EpisodeOfCare id from extension, context.related, or SP migration context.encounter. */
export function getEpisodeOfCareIdFromDocumentReference(doc: DocumentReference): string | undefined {
  const ext = doc.extension?.find((e) => e.url === EPISODE_OF_CARE_CONTEXT_EXT);
  const extRef = ext?.valueReference?.reference;
  if (extRef?.startsWith('EpisodeOfCare/')) {
    return extRef.replace('EpisodeOfCare/', '');
  }

  for (const r of doc.context?.related ?? []) {
    const ref = r.reference;
    if (ref?.startsWith('EpisodeOfCare/')) {
      return ref.replace('EpisodeOfCare/', '');
    }
  }

  for (const r of doc.context?.encounter ?? []) {
    const ref = r.reference;
    if (ref?.startsWith('EpisodeOfCare/')) {
      return ref.replace('EpisodeOfCare/', '');
    }
  }

  return undefined;
}
